import useSEO from "@/components/site/useSEO";
import PageHero from "@/components/site/PageHero";
import { ScreenBlock, CTABand, SectionHead } from "@/components/site/ui";
import { ManagerDashboard } from "@/components/site/mockups";

const IMG = "https://images.unsplash.com/photo-1762955911431-4c44c7c3f408?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function ResidentialCare() {
  useSEO(
    "Residential Care Management Software | AYOBELL",
    "AYOBELL connects resident records, care planning, medication, observations, staffing, training and governance across your care home with manager dashboards."
  );
  return (
    <>
      <PageHero
        eyebrow="Residential Care"
        title="Connected care across your home."
        subtitle="AYOBELL brings resident information, care planning, medication and daily records together — with the oversight managers need across the whole home."
        primary={{ to: "/contact", label: "Book a Demo" }}
        secondary={{ to: "/platform", label: "Explore the Platform" }}
        screenKey="dashboard"
        screenComp={ManagerDashboard}
      />

      <section className="bg-white py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="relative rounded-3xl overflow-hidden h-64 sm:h-80">
            <img src={IMG} alt="Care professional supporting a resident in a care home" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <ScreenBlock
            eyebrow="Resident records"
            title="Resident information in one connected record"
            desc="Everything the team needs about each resident, from their overview to consent and important people."
            points={["Resident / person overview", "About Me & important people", "Documents & consent", "Alerts and risks"]}
            screenKey="serviceusers"
          />
        </div>
      </section>

      <section className="bg-white py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <ScreenBlock
            reverse
            eyebrow="Care planning & medication"
            title="Care plans, medication and daily records"
            desc="Plan person-centred care, record medication and capture daily notes and observations as care is delivered."
            points={["Person-centred care plans", "Medication records & MAR / eMAR", "Daily notes", "Observations"]}
            screenKey="careplans"
          />
        </div>
      </section>

      <section className="bg-slate-50 py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <ScreenBlock
            eyebrow="People & quality"
            title="Staff management, training and governance"
            desc="Manage your team's rota, training and supervisions, and keep incidents and safeguarding visible for quality oversight."
            points={["Staff records & rota", "Training compliance", "Supervisions & appraisals", "Incidents & safeguarding"]}
            screenKey="supervision"
          />
        </div>
      </section>

      <section className="bg-white py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <SectionHead center eyebrow="Oversight" title="Management dashboards for the whole home" desc="See the status of care, people and governance at a glance." />
          <div className="mt-14">
            <ScreenBlock
              reverse
              title="Home-level oversight for managers"
              desc="Manager dashboards summarise operational and governance information so nothing slips through the cracks."
              points={["Manager dashboards", "Operational summaries", "AYOBELL CQC readiness dashboard", "Governance reporting"]}
              screenKey="cqc"
              url="app.ayobell.com/governance"
            />
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
